import { cellAtWorld, heroWalkable } from '../grid.ts';
import { dist2, idiv, imax, imin, isqrt, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { MouthsRules, WolfRules, WolfState } from '../roca-types.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { archBest, archBlow } from '../battle/roca-layout.ts';
import { ROCA_HORN } from '../battle/climb.ts';
import { mouthsShut } from '../battle/mouths.ts';
import { WOLF_PILLAR, WOLF_STONE, wolfDropAt, wolfExitAt } from '../battle/graywolf.ts';
import { sendaMark } from '../battle/senda.ts';
import { useSkills } from './ghost.ts';

/**
 * The lab's bots for the Long Hunt's chapter 5 (docs/sendas/barbaro.md, «La guarida»; docs/SENDAS.md, «Calibración»),
 * asked by ai/isla-bot.ts:
 *
 * - the climb (`climb`): who knows keeps by Ulla and clears what is on her before going on, cuts the howler's howl and
 *   blows the horn when she is swarmed; who doesn't runs up the ledges fighting what stands in its way.
 * - the mouths (`mouths`): who knows reads the tracks (the one without the Wolf's) and strikes the puzzle's best set in
 *   an order that keeps it standing; who doesn't strikes the arch nearest until two are down.
 * - the ash bed (`bed`): both clear each chamber's band and go on down.
 * - the Grey Wolf (`wolf`): who knows stands at the way out it looks at (the mouth, the edge), cuts its howls with the
 *   pillars, the stones, the horn or its blows, cracks the slabs on the way to the open mouths, and in the last run goes
 *   after it for the boat; who doesn't fights it where it is and runs after it when it runs. Both step off a leap's
 *   shadow (anyone sees one).
 *
 * It only moves the hero, taps what a player would and picks its target; nothing it does is outside the rules.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

const ullaOf = (s: BattleState): { x: number; y: number; hp: number } | undefined => s.senda?.npcs.find((n) => n.id === 'ulla');

/* ---------- the climb ---------- */

const climbBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.roca?.climb;
  const w = s.senda?.roca?.climb;
  if (!r || !w || w.done >= 0) return;
  const u = ullaOf(s);
  const ahead = r.way[imin(r.way.length - 1, w.far + 4)] as Pt;
  const hw = findEnemy(s, w.howler.id);
  if (knows) {
    // The howler's howl: cut it.
    if (hw && hw.hp > 0 && (w.howler.phase === 2 || w.howler.phase === 3) && withinRange(h.x, h.y, hw.x, hw.y, 7000)) return strike(s, h, hw);
    if (u && u.hp > 0) {
      const on = enemiesNear(s, u.x, u.y, 1700, (e) => targetable(s, e));
      if (w.horn > 0 && on.length >= 4) sendaMark(s, h, ROCA_HORN, 0);
      const t = nearestTo(enemiesNear(s, u.x, u.y, 3200, (e) => targetable(s, e)), u.x, u.y);
      if (t) return strike(s, h, t);
      // Never far ahead of her.
      if (!withinRange(h.x, h.y, u.x, u.y, 2600)) {
        h.target = 0;
        return go(s, h, u.x, u.y, 400);
      }
    }
  } else {
    const t = nearestTo(enemiesNear(s, h.x, h.y, 1500, (e) => targetable(s, e)), h.x, h.y);
    if (t) return strike(s, h, t);
  }
  const near = nearestTo(enemiesNear(s, h.x, h.y, 1200, (e) => targetable(s, e)), h.x, h.y);
  if (near) return strike(s, h, near);
  h.target = 0;
  h.forced = false;
  go(s, h, (w.far >= r.way.length - 5 ? r.mouth : ahead).x, (w.far >= r.way.length - 5 ? r.mouth : ahead).y);
};

/* ---------- the mouths ---------- */

/** The blows (arch by arch) that bring the puzzle's best set down with the one without tracks standing, shortest first. */
const plan = (r: MouthsRules, from: number[]): number[] => {
  const { best, sets } = archBest(from, r.links, r.trace, r.breaks);
  const want = sets[0] ?? 0;
  const key = (c: number[]): string => c.join('');
  const prev = new Map<string, { k: string; i: number } | null>([[key(from), null]]);
  const states: number[][] = [from.slice()];
  for (let n = 0; n < states.length; n++) {
    const c = states[n] as number[];
    let bits = 0;
    let down = 0;
    c.forEach((v, i) => {
      if (v >= r.breaks) {
        bits |= 1 << i;
        down++;
      }
    });
    if (down === best && bits === want) {
      const out: number[] = [];
      let k = key(c);
      for (let p = prev.get(k); p; p = prev.get(k)) {
        out.unshift(p.i);
        k = p.k;
      }
      return out;
    }
    for (let i = 0; i < c.length; i++) {
      if ((c[i] as number) >= r.breaks || i === r.trace) continue;
      const d = archBlow(c, r.links, i, r.breaks);
      if ((d[r.trace] as number) >= r.breaks) continue;
      const kd = key(d);
      if (prev.has(kd)) continue;
      prev.set(kd, { k: key(c), i });
      states.push(d);
    }
  }
  return [];
};

const mouthsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.roca?.mouths;
  const w = s.senda?.roca?.mouths;
  if (!r || !w || w.done >= 0) return;
  const u = ullaOf(s);
  // Ulla swarmed, or the hero: that first.
  const press = u && u.hp > 0 ? enemiesNear(s, u.x, u.y, 1500, (e) => targetable(s, e)) : [];
  const mine = enemiesNear(s, h.x, h.y, 1100, (e) => targetable(s, e));
  if (press.length >= 2 || mine.length >= 3) {
    if (w.horn > 0 && press.length >= 4) sendaMark(s, h, ROCA_HORN, 0);
    const t = nearestTo(press.length ? press : mine, u && press.length ? u.x : h.x, u && press.length ? u.y : h.y);
    if (t) return strike(s, h, t);
  }
  if (w.job >= 0) return;
  let next = -1;
  if (knows) next = plan(r, w.cracks)[0] ?? -1;
  if (next < 0 && mouthsShut(w) < r.need) {
    // (Who doesn't read the tracks: the arch nearest that is still up. Who does, with no plan left that keeps the one
    // without tracks: the nearest whose blow doesn't bring that one down.)
    let bd = 0x7fffffff;
    r.arches.forEach((p, k) => {
      if (w.shut[k]) return;
      if (knows && (k === r.trace || (archBlow(w.cracks, r.links, k, r.breaks)[r.trace] as number) >= r.breaks)) return;
      const d = dist2(p.x, p.y, h.x, h.y);
      if (d < bd) {
        bd = d;
        next = k;
      }
    });
  }
  if (next >= 0) {
    h.target = 0;
    h.forced = false;
    sendaMark(s, h, next, 0);
    return;
  }
  const t = nearestTo(enemiesNear(s, h.x, h.y, 9000, (e) => targetable(s, e)), h.x, h.y);
  if (t) strike(s, h, t);
};

/* ---------- the ash bed ---------- */

const bedBot = (s: BattleState, h: Hero): void => {
  const r = s.rules?.senda?.roca?.bed;
  const w = s.senda?.roca?.bed;
  if (!r || !w || w.done >= 0) return;
  const t = nearestTo(enemiesNear(s, h.x, h.y, 6000, (e) => targetable(s, e)), h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
  h.forced = false;
  const room = r.rooms[w.cleared];
  const to = w.cleared >= r.rooms.length ? r.bed : (room?.at ?? r.bed);
  go(s, h, to.x, to.y);
};

/* ---------- the Grey Wolf ---------- */

/** Where to stand to guard a way out: on the floor before it, toward the middle. */
const guardAt = (r: WolfRules, kind: number, k: number): Pt => {
  const p = wolfExitAt(r, kind, k);
  const c = kind === 0 ? r.bed : r.summit;
  const dx = c.x - p.x;
  const dy = c.y - p.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  return { x: p.x + idiv(dx * 1000, d), y: p.y + idiv(dy * 1000, d) };
};

/** Off a leap's shadow: away from its middle, onto floor (near an edge, toward the middle of where it fights). */
const dodgeLeap = (s: BattleState, r: WolfRules, w: WolfState, h: Hero): boolean => {
  if ((w.mode !== 5 && w.mode !== 6) || !withinRange(h.x, h.y, w.leapAt.x, w.leapAt.y, r.leap.r + 500)) return false;
  const mid = w.phase === 1 ? r.bed : r.summit;
  let dx = h.x - w.leapAt.x;
  let dy = h.y - w.leapAt.y;
  if (dx === 0 && dy === 0) {
    dx = mid.x - h.x;
    dy = mid.y - h.y + 1;
  }
  const d = imax(1, isqrt(dx * dx + dy * dy));
  const tries: Pt[] = [];
  // Straight away, then a quarter either side, then toward the middle.
  for (const [cx, cy] of [
    [dx, dy],
    [-dy, dx],
    [dy, -dx],
  ] as const)
    tries.push({ x: w.leapAt.x + idiv(cx * 2300, d), y: w.leapAt.y + idiv(cy * 2300, d) });
  const md = imax(1, isqrt(dist2(mid.x, mid.y, w.leapAt.x, w.leapAt.y)));
  tries.push({ x: w.leapAt.x + idiv((mid.x - w.leapAt.x) * 2300, md), y: w.leapAt.y + idiv((mid.y - w.leapAt.y) * 2300, md) });
  const to = tries.find((p) => {
    const c = cellAtWorld(s, p.x, p.y);
    return c >= 0 && heroWalkable(s, c);
  });
  if (!to) return false;
  h.target = 0;
  h.forced = false;
  go(s, h, to.x, to.y, 200);
  return true;
};

/** A pillar or a stone near it to bring down on its howl (null: none in time). */
const dropFor = (s: BattleState, r: WolfRules, w: WolfState, h: Hero, e: Enemy): number => {
  const left = w.until - s.tick + (w.mode === 7 ? r.howl.howl : 0);
  let best = -1;
  let bt = 0x7fffffff;
  const list = w.phase === 1 ? r.pillars.map((p, k) => ({ p, at: WOLF_PILLAR + k, up: w.pillars[k] })) : r.stones.map((p, k) => ({ p, at: WOLF_STONE + k, up: w.stones[k] }));
  for (const x of list) {
    if (!x.up || !withinRange(e.x, e.y, x.p.x, x.p.y, r.drop.r - 300)) continue;
    const at = wolfDropAt(r, x.at) as Pt;
    const t = idiv(isqrt(dist2(h.x, h.y, at.x, at.y)), 150) + r.drop.strike + 4;
    if (t < left && t < bt) {
      bt = t;
      best = x.at;
    }
  }
  return best;
};

const wolfBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.roca?.wolf;
  const w = s.senda?.roca?.wolf;
  if (!r || !w || w.done >= 0) return;
  const e = findEnemy(s, w.id);
  if (dodgeLeap(s, r, w, h)) return;
  if (!e || e.hp <= 0) return;
  const swarm = enemiesNear(s, h.x, h.y, 1300, (x) => targetable(s, x) && x.id !== w.id);
  // Up the crack after it.
  if (w.mode === 9) {
    h.target = 0;
    h.forced = false;
    return go(s, h, r.crackTop.x, r.crackTop.y - 600, 400);
  }
  if (!knows) {
    // Where it is: at it; running, after it.
    if (w.mode === 3) {
      const t = nearestTo(enemiesNear(s, h.x, h.y, 6000, (x) => targetable(s, x)), h.x, h.y);
      if (t) return strike(s, h, t);
      return;
    }
    if (swarm.length >= 4 && !withinRange(h.x, h.y, e.x, e.y, 1600)) return strike(s, h, nearestTo(swarm, h.x, h.y) as Enemy);
    return strike(s, h, e);
  }
  // Its howl: cut it (on it already: its own blows; else a pillar or a stone; else the horn).
  if (w.mode === 7 || w.mode === 8) {
    if (w.job >= 0) return;
    // (Its blows alone rarely cut it: the roof, the edge or the horn first.)
    const d = dropFor(s, r, w, h, e);
    if (d >= 0) {
      h.target = 0;
      h.forced = false;
      sendaMark(s, h, d, 0);
      return;
    }
    if (w.horn > 0 && w.mode === 8) sendaMark(s, h, ROCA_HORN, 0);
    return strike(s, h, e);
  }
  // Looking at a way out, or running for it: there first.
  if (w.mode === 1 || w.mode === 2 || w.mode === 11) {
    const g = guardAt(r, w.exitKind, w.exit);
    if (!withinRange(h.x, h.y, g.x, g.y, 500)) {
      h.target = 0;
      h.forced = false;
      return go(s, h, g.x, g.y, 200);
    }
    h.path = [];
    h.moving = false;
    if (withinRange(h.x, h.y, e.x, e.y, 1100)) return strike(s, h, e);
    const t = nearestTo(enemiesNear(s, h.x, h.y, 1000, (x) => targetable(s, x)), h.x, h.y);
    if (t) {
      h.target = t.id;
      h.forced = true;
    }
    return;
  }
  // Under the water: the slabs on the way to the open mouths cracked to two, the cubs.
  if (w.mode === 3) {
    if (w.phase === 1) {
      const k = r.slabs.findIndex((x, i) => r.open[x.mouth] && (w.slabs[i] as number) < 2 && i !== w.onSlab);
      const sl = r.slabs[k];
      if (sl && w.until - s.tick > 30) {
        h.target = 0;
        h.forced = false;
        return go(s, h, sl.at.x, sl.at.y, 150);
      }
    }
    const t = nearestTo(enemiesNear(s, h.x, h.y, 7000, (x) => targetable(s, x)), h.x, h.y);
    if (t) return strike(s, h, t);
    const g = guardAt(r, w.exitKind, w.exit);
    return go(s, h, g.x, g.y, 300);
  }
  // A swarm of cubs on the hero: a whirl at them; else the Wolf.
  if (swarm.length >= 4) return strike(s, h, nearestTo(swarm, h.x, h.y) as Enemy);
  strike(s, h, e);
};

/** The chapter's act this bot plays (false: not one of its). */
export const rocaBot = (s: BattleState, h: Hero, knows: boolean): boolean => {
  const r = s.rules?.senda?.roca;
  if (r?.climb) climbBot(s, h, knows);
  else if (r?.mouths) mouthsBot(s, h, knows);
  else if (r?.bed) bedBot(s, h);
  else if (r?.wolf) wolfBot(s, h, knows);
  else return false;
  return true;
};
